import * as React from "react";

import {observer} from "mobx-react";
import {autorun} from "mobx";
import EsriGraphic from "@arcgis/core/Graphic";
import EsriGraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import EsriPoint from "@arcgis/core/geometry/Point";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import FillSymbolProperties = __esri.FillSymbolProperties;
import {Button, Dimmer, Item, Loader} from "semantic-ui-react";
import {NavigationConfiguration} from "../../../config/navigation-configuration";
import {AppConfiguration} from "../../../core/config/app-configuration";
import {Icon} from "../../../core/components/icon/icon";
import {Grundstueck, Bodenbedeckung, Zone} from "../../../models/grundstueck";
import {Gebaeude, Eingang} from "../../../models/gebaeude";
import {Projekt} from "../../../models/projekt";
import {Adresse} from "./aside/adresse";
import {ObjectResultGrundstueck} from "./object-result-grundstueck";
import {ObjectResultGebaeude} from "./object-result-gebaeude";
import {ObjectResultProjekt} from "./object-result-projekt";
import {MapUtils} from "../../../utils/map-utils";
import {withStore} from "../../../core/stores/abstract-app-store";
import {MapViewerStore} from "../../../stores/map-viewer-store-mobx";

export interface ObjectResultProps {
    store?: MapViewerStore; // this is injected by withStore
}

@observer
class ObjectResultBase extends React.Component<ObjectResultProps, any> {
    watchHandles: any[] = [];
    appConfig?: AppConfiguration;
    highlightLayers: EsriGraphicsLayer[] = [];
    constructor(props: ObjectResultProps) {
        super(props);
        this.state = {
            loading: false,
            featureGraphic: null,
            topicId: "objektwesen",
            highlightedItemId: null
        };
        this.appConfig = props.store?.appConfig;
        this.highlightLayers.push(new EsriGraphicsLayer({listMode: "hide", title: "highlightLayer 1"}));
        this.highlightLayers.push(new EsriGraphicsLayer({listMode: "hide", title: "highlightLayer 2"}));
        this.highlightLayers.push(new EsriGraphicsLayer({listMode: "hide", title: "highlightLayer 3"}));
    }
    handlePrepareKontakt(grundstueck: Grundstueck | undefined, type: "grundstueck" | "gebaeude" | "projekt",
                         store: MapViewerStore): void {
        if(!store){
            return;
        }
        const kontaktItems: React.ReactElement[] = [];
        if (grundstueck && type === "grundstueck"){
            kontaktItems.push(<Adresse key={"nachfuehrungskreis"} adresse={grundstueck.nachfuehrungskreis}/>);
            kontaktItems.push(<Adresse key={"grundbuchkreis"} adresse={grundstueck.grundbuchkreis}/>);
        } else if (type === "gebaeude") {
            kontaktItems.push(<Adresse key={"gvl"} adresse={"gvl"} />);
            if (grundstueck){
                kontaktItems.push(<Adresse key={"gemeindekanzlei"} adresse={grundstueck.gemeindekanzlei}/>);
            }
        }else if (type === "projekt") {
            if (grundstueck){
                kontaktItems.push(<Adresse key={"bauamt"} adresse={grundstueck.bauamt}/>);
            }
            kontaktItems.push(<Adresse key={"rawi"} adresse={"rawi"}/>);
        }
        const kontakt: React.ReactElement =
            <div>{(type!=="projekt"? (<h2>Kontakte zu diesem {(type==="gebaeude"? "Gebäude": "Grundstück")}</h2>): "")}
                <Item.Group divided>
                    {kontaktItems}
                </Item.Group>
            </div>;
        store.appStore.setAsideContent(kontakt);
    }
    errorAndNotFound(type: "grundstueck" | "gebaeude" | "projekt"): void {
        const { store } = this.props;
        if(!store){
            return;
        }
        const { appStore, oStore } = store;
        const breadcrumbs = new NavigationConfiguration();
        let errorTitle: string;
        let breadcrumbTitle: string;
        switch (type) {
        case "grundstueck":
            errorTitle = "Grundstück nicht gefunden!";
            breadcrumbTitle = "Grundstück";
            break;
        case "gebaeude":
            errorTitle = "Gebäude nicht gefunden!";
            breadcrumbTitle = "Gebäude";
            break;
        case "projekt":
            errorTitle = "Projekt nicht gefunden!";
            breadcrumbTitle = "Projekt";
            break;
        default:
            errorTitle = "Objekt nicht gefunden!";
            breadcrumbTitle = "Objekt";
        }
        if (oStore.status !== "not-found") {
            errorTitle = "Es ist ein Fehler aufgetreten!";
        }
        breadcrumbs.items = [
            { title: breadcrumbTitle }
        ];
        appStore.setPageTitle(errorTitle);
        appStore.setTopContent(null);
        appStore.setBottomContent(null);
        appStore.setAsideContent(null);
        appStore.setBreadcrumbs(breadcrumbs);
        appStore.setTopicById(type);
    }
    handleHighlight(item: Grundstueck | Gebaeude | Projekt, geometry: __esri.Geometry | undefined,
                    store?: MapViewerStore): void{
        if (!store || !store?.mapStore?.view) {
            return;
        }
        const mapViewerConfig = store.mapViewerConfig;
        let highlightSymbol;
        let target: __esri.Geometry | null = null;
        this.highlightLayers.forEach((highlightLayer) => { highlightLayer.removeAll(); });
        if (item.class === "Gebaeude" && typeof geometry !=="undefined" && geometry.type === "point") {
            highlightSymbol = mapViewerConfig.pointerSymbols?.coords;
            // auf Grundstück zoomen, falls vorhanden
            let grundstueck: Grundstueck | undefined | null;
            if ("egrid" in item && item.egrid) {
                grundstueck = store.oStore.getGrundstueck(item.egrid);
            }
            if (grundstueck) {
                target = grundstueck.geometry as __esri.Geometry;
            } else {
                // falls kein Grundsück vorhanden auf Punkt zentrieren
                target = geometry;
            }
        } else {
            highlightSymbol = mapViewerConfig.highlightSymbol as FillSymbolProperties;
        }
        if (item.class==="Gebaeude" && typeof store.oStore.edid !== "undefined" && "eingaenge" in item) {
            // Gebäudeeingang markieren, falls dieser gesetzt ist
            const eingang = item.eingaenge.find((ein: Eingang) => ein.edid === store?.oStore.edid);
            if (eingang) {
                this.highlightLayers[1].add(
                    new EsriGraphic({geometry: eingang.geometry,
                        symbol: mapViewerConfig.pointerSymbols?.eingang})
                );
            }
        } else if (item.class==="Projekt") {
            this.highlightLayers[2].add(
                new EsriGraphic({geometry: item.geometry,
                    symbol: mapViewerConfig.pointerSymbols?.coords})
            );
        }
        store.PopupStore.setVisible(false);
        const graphic = new EsriGraphic({geometry,
            symbol: highlightSymbol});
        this.highlightLayers[0].add(graphic);
        this.setState({featureGraphic: graphic});

        // Falls noch kein "center" gesetzt ist, das "center" aus der Geometrie verwenden
        if(this.appConfig && !this.appConfig?.mapView?.center){
            if(this.appConfig?.mapView.viewpoint){
                delete this.appConfig.mapView.viewpoint;
                this.appConfig.mapView.extent = MapUtils.calculateMinExtent(graphic.geometry, 50).toJSON();
            }else if(graphic.geometry.extent){
                this.appConfig.mapView.center = new EsriPoint({
                    x: graphic.geometry.extent.center.x,
                    y: graphic.geometry.extent.center.y,
                    spatialReference: this.appConfig?.mapView.spatialReference
                });
            }
        }
        if(!target && graphic) {
            target = graphic.geometry as __esri.Geometry;
        }
        if(target) {
            reactiveUtils.when(
                () => store?.mapStore?.view?.ready,
                () => {
                    this.goToGeometries(target as __esri.Geometry, store);
                },
                {initial: true, once: true}
            );
        }
    }
    goToGeometries(geometries: __esri.Geometry | __esri.Geometry[], store?: MapViewerStore): void {
        if (!store?.mapStore.view) {
            return;
        }
        store.mapStore.view?.goTo(MapUtils.calculateMinExtent(geometries, 50)).catch((error) => {
            if (error.name !== "view:goto-interrupted") {
                // eslint-disable-next-line no-console
                console.error(error);
            }
        });
    }
    handleHighlightClick(e: React.MouseEvent | undefined,
                         item: Bodenbedeckung | Zone | Eingang | __esri.Point | null,
                         layerNo?: number | undefined): void {
        const {featureGraphic} = this.state;
        const { store} = this.props;
        if(!store){
            return;
        }
        const {showMap} = store.oStore;
        const mapViewerConfig = store.mapViewerConfig;
        if (!showMap) {
            store.oStore.setShowMap(true);
        }
        if (featureGraphic && typeof store.mapStore.view !== "undefined") {
            store?.PopupStore.setVisible(false);
            // nur zoomen, wenn die Funktion durch den Nutzer (event) ausgelöst wurde
            if (e && showMap) {
                store.mapStore.view.goTo(featureGraphic).catch((error) => {
                    if (error.name !== "view:goto-interrupted") {
                        // eslint-disable-next-line no-console
                        console.error(error);
                    }
                });
            }
        }
        const lNo = (typeof layerNo !== "undefined"? layerNo: 1);
        this.highlightLayers[lNo].removeAll();
        this.highlightLayers[lNo].opacity = 1;
        if (item) {
            let geometry;
            if (item.hasOwnProperty("x")){
                geometry = item;
            } else if ("geometry" in item) {
                if (Array.isArray(item.geometry)) {
                    if (item.geometry.length > 1) {
                        geometry = geometryEngine.union(item.geometry);
                    } else if (item.geometry.length === 1) {
                        geometry = item.geometry[0];
                    }
                } else {
                    geometry = item.geometry;
                }
            }
            if (geometry) {
                let highlightSymbol;
                let layerOpacity = 1;
                if (geometry.type === "point") {
                    if (item.hasOwnProperty("edid")){
                        highlightSymbol = mapViewerConfig.pointerSymbols?.eingang;
                    } else {
                        highlightSymbol = mapViewerConfig.pointerSymbols?.coords;
                    }
                } else {
                    // Zonenplan Symbol verwenden, falls vorhanden
                    if ("class" in item && "typeGemeinde" in item && item.class === "Zone") {
                        highlightSymbol = store.oStore.getZonenplanSymbol(item.typeGemeinde);
                        layerOpacity = 0.7;
                    }
                    if (!highlightSymbol){
                        layerOpacity = 1;
                        highlightSymbol = {
                            type: "simple-fill",
                            outline: {
                                cap: "round",
                                join: "round",
                                width: 0,
                                color: [0, 255, 0, 1]
                            },
                            color: [0, 255, 0, 0.25]
                        } as FillSymbolProperties;
                    }
                }
                this.highlightLayers[lNo].add(new EsriGraphic({geometry,
                    symbol: highlightSymbol}));
                this.highlightLayers[lNo].opacity = layerOpacity;
            }
            if ("itemId" in item) {
                this.setState({highlightedItemId: item.itemId});
            }
        } else {
            this.setState({highlightedItemId: null});
        }
        if (e) {
            e.preventDefault();
        }
    }
    handleZoomInClick(e: React.MouseEvent): void {
        const { store } = this.props;
        const {featureGraphic} = this.state;
        if(!store) {
            return;
        }
        const {showMap} = store.oStore;
        if (featureGraphic && typeof store.mapStore.view !== "undefined") {
            store?.PopupStore.setVisible(false);
            if (!showMap) {
                store.oStore.setShowMap(true);
            }
            store.mapStore.view.goTo(featureGraphic).catch((error) => {
                if (error.name !== "view:goto-interrupted") {
                    // eslint-disable-next-line no-console
                    console.error(error);
                }
            });
            e.preventDefault();
            // Wenn blur direkt via e.target aufgerufen wird, reklamiert Webstorm:
            // TS2339: Property 'blur' does not exist on type 'EventTarget'.
            const target = e.target as HTMLElement;
            target.blur();
        }
    }
    handleToggleMap(e: React.MouseEvent): void {
        const { store } = this.props;
        if(!store){
            return;
        }
        const {showMap} = store.oStore;
        store?.oStore.setShowMap(!showMap);
        e.preventDefault();
        // Wenn blur direkt via e.target aufgerufen wird, reklamiert Webstorm:
        // TS2339: Property 'blur' does not exist on type 'EventTarget'.
        const target = e.target as HTMLElement;
        target.blur();
    }
    componentDidMount(): void {
        const { store} = this.props;
        // rerun until map view is ready and search view model is loaded
        autorun((innerReaction: any) => {
            if (!store || !store.mapStore.view) {
                return;
            }
            const {objectId, topic} = store.appStore;
            this.highlightLayers.forEach((highlightLayer) => { store.mapStore.view?.map.add(highlightLayer); });
            // TODO: Bei nächsten API Update überprüfen ob es wieder mit der ESRI trigger-action funktioniert
            // popup.popup.viewModel.on("trigger-action", (event: any) => {
            window.addEventListener("geo-trigger-action", (event: any) => {
                const actionID = event.detail.id; // event.action.id
                if (store.mapStore.selectedFeature !== null) {
                    if (actionID === "objektlu") {
                        store.oStore.requestGrundstueck(
                            store.mapStore.selectedFeature.getAttribute("EGRIS_EGRID"));
                        store.PopupStore.setVisible(false);
                    } else if (actionID === "objektlu-p") {
                        store.oStore.requestProjekt(
                            store.mapStore.selectedFeature.getAttribute("BAGENR"));
                        store.PopupStore.setVisible(false);
                    } else if (actionID === "objektlu-g") {
                        store.oStore.requestGebaeude(
                            store.mapStore.selectedFeature.getAttribute("GWR_EGID"),
                            store.mapStore.selectedFeature.getAttribute("GWR_EDID"));
                        store.PopupStore.setVisible(false);
                    }
                }
            });
            if (typeof objectId !== "undefined") {
                if (topic.id === "grundstueck") {
                    store.oStore.requestGrundstueck(objectId);
                } else if (topic.id === "bauprojekt") {
                    store.oStore.requestProjekt(objectId);
                }
            }
            innerReaction.dispose();
        });
    }
    showGrundstueck(egrid: string, event?: React.MouseEvent | null): void {
        const { store } = this.props;
        store?.oStore.requestGrundstueck(egrid);
        if (event) {
            event.preventDefault();
            document.getElementById("geo-page-title")?.scrollIntoView({behavior: "smooth"});
        }
    }
    showGebaeude(egid: number, event?: React.MouseEvent | null): void {
        const { store } = this.props;
        store?.oStore.requestGebaeude(egid, undefined);
        if (event) {
            event.preventDefault();
            document.getElementById("geo-page-title")?.scrollIntoView({behavior: "smooth"});
        }
    }
    showProjekt(bageNr: string, event?: React.MouseEvent | null): void {
        const { store } = this.props;
        store?.oStore.requestProjekt(bageNr);
        if (event) {
            event.preventDefault();
            document.getElementById("geo-page-title")?.scrollIntoView({behavior: "smooth"});
        }
    }
    componentWillUnmount(): void {
        const { store } = this.props;
        this.watchHandles.forEach((handle: any) => {
            handle.remove();
        });
        this.watchHandles.length = 0;
        if (!store || !store.mapStore.view) {
            return;
        }
        this.highlightLayers.forEach((highlightLayer) => {
            highlightLayer.removeAll();
            store.mapStore.view?.map.remove(highlightLayer);
        });
    }
    render(): React.ReactElement | null {
        const { featureGraphic, subtopicId } = this.state;
        const { store } = this.props;
        if( !store ){
            return <></>;
        }
        if (!["grundstueck", "gebaeude", "bauprojekt"].includes(subtopicId)) {
        //    return null;
        }
        let result: React.ReactElement | null = null;
        if(store?.oStore.selectedObjektType === "grundstueck") {
            result = (<ObjectResultGrundstueck
                key={store.oStore.selectedObjektId}
                grundstueck={store.oStore.selectedGrundstueck()}
                handleHighlightClick={(e: React.MouseEvent | undefined, item: Bodenbedeckung | Zone | null,
                                       layerNo: number) => this.handleHighlightClick(e, item, layerNo)}
                prepareKontakt={(grudstueck: Grundstueck) =>
                    this.handlePrepareKontakt(grudstueck, "grundstueck", store)}
                handleErrorAndNotFound={() => this.errorAndNotFound("grundstueck")}
                handleHighlight={(grudstueck: Grundstueck, geometry: __esri.Geometry) =>
                    this.handleHighlight(grudstueck, geometry, store)}
                handleShowGebaeude={(egid: number, e: React.MouseEvent)=>this.showGebaeude(egid, e)}
                handleShowProjekt={(bagenr: string, e: React.MouseEvent)=>this.showProjekt(bagenr, e)}
            />);
        } else if(store.oStore.selectedObjektType === "gebaeude") {
            result = (<ObjectResultGebaeude
                key={store.oStore.selectedObjektId}
                gebaeude={store.oStore.selectedGebaeude()}
                handleHighlightClick={(e: React.MouseEvent | undefined, item: Eingang | __esri.Point | null,
                                       layerNo: number) => this.handleHighlightClick(e, item, layerNo)}
                prepareKontakt={(grundstueck: Grundstueck | undefined) =>
                    this.handlePrepareKontakt(grundstueck, "gebaeude", store)}
                handleErrorAndNotFound={() => this.errorAndNotFound("gebaeude")}
                handleHighlight={(gebaeude: Gebaeude, geometry: __esri.Geometry) =>
                    this.handleHighlight(gebaeude, geometry, store)}
                handleShowGrundstueck={(egrid: string, e: React.MouseEvent)=>this.showGrundstueck(egrid, e)}
            />);
        } else if(store.oStore.selectedObjektType === "projekt") {
            result = (<ObjectResultProjekt
                key={store.oStore.selectedObjektId}
                projekt={store.oStore.selectedProjekt()}
                handleHighlightClick={(e: React.MouseEvent | undefined, item: Eingang | __esri.Point | null,
                                       layerNo: number) => this.handleHighlightClick(e, item, layerNo)}
                prepareKontakt={(grundstueck: Grundstueck | undefined) =>
                    this.handlePrepareKontakt(grundstueck, "projekt", store)}
                handleErrorAndNotFound={() => this.errorAndNotFound("projekt")}
                handleHighlight={(projekt: Projekt, geometry: __esri.Geometry) =>
                    this.handleHighlight(projekt, geometry, store)}
                handleShowGrundstueck={(egrid: string, e: React.MouseEvent)=>this.showGrundstueck(egrid, e)}
                handleShowProjekt={(bagenr: string, e: React.MouseEvent)=>this.showProjekt(bagenr, e)}
            />);
        }
        return(
            <div className="thematic-result">
                {store.oStore.loading && <Dimmer active inverted>
                    <Loader inverted>Loading</Loader>
                </Dimmer>}
                <div className={"geo-map-buttons"}>
                    {store.oStore.selectedObjektType &&
                <Button className={"map"} secondary
                    onClick={(e: React.MouseEvent) =>
                        this.handleToggleMap(e)}>
                    <Icon name={"map"} selected/> {store.oStore.showMap ? "Karte ausblenden" : "Karte einblenden"}
                </Button>}
                    {featureGraphic && store.oStore.showMap &&
                <Button className={"zoom"} secondary
                    onClick={(e: React.MouseEvent) =>
                        this.handleZoomInClick(e)}>
                    <Icon name={"zoom_in"} selected /> Zoomen
                </Button>}
                </div>
                {result}
            </div>
        );
    }
}

export const ObjectResult = withStore(ObjectResultBase);
